import { OfflineParsedQuery } from "./types";
import { tokenize, toAscii } from "./shared/tokenizer";

const WEEKDAYS: Record<string, number> = {
  poniedzialek: 0, poniedzia: 0,
  wtorek: 1,
  sroda: 2, srode: 2,
  czwartek: 3,
  piatek: 4, piatku: 4,
  sobota: 5, sobote: 5,
  niedziela: 6, niedziele: 6,
};

const MONTHS: Record<string, number> = {
  stycznia: 1, styczen: 1,
  lutego: 2, luty: 2,
  marca: 3, marzec: 3,
  kwietnia: 4, kwiecien: 4,
  maja: 5,
  czerwca: 6, czerwiec: 6,
  lipca: 7, lipiec: 7,
  sierpnia: 8, sierpien: 8,
  wrzesnia: 9, wrzesien: 9,
  pazdziernika: 10, pazdziernik: 10,
  listopada: 11, listopad: 11,
  grudnia: 12, grudzien: 12,
};

const ARRANGEMENT_MARKERS = [
  "kto ma", "co ustal", "co ustali", "ustalilem", "ustalilismy",
  "termin", "do kiedy", "na kiedy", "do konca",
  "ma zrobic", "ma zrobic", "musi wykonac", "ma przygotowac",
  "spotkanie", "kontrola", "raport do", "faktura do", "zwrot do",
  "czy mam dzis", "czy mam dzisiaj", "z kim mam", "kto sie spoznia",
  "kto opoznia", "opoznienie", "ustalenia", "co uzgodnilismy",
  "co uzgodnilem", "dostarczyc", "przygotowac", "wymienic", "sprawdzic"
];

const ARRANGEMENT_VERBS = [
  "dostarczyc", "zrobic", "przygotowac", "wyslac", "zorganizowac",
  "odebrac", "odestac", "spisac", "przyniesc", "zglosic", "przeslac",
  "wymienic", "sprawdzic", "przeglad"
];

const ARRANGEMENT_STOPWORDS = new Set([
  "kto", "ma", "mam", "musi", "powinien", "trzeba", "nalezy",
  "czy", "dzis", "dzisiaj", "jutro", "wczoraj", "tydzien", "miesiac",
  "kiedy", "termin", "deadline", "do", "na", "w", "z", "za", "od", "kiedy"
]);

const NAME_ALIASES: Record<string, string> = {
  ania: "Anna", anka: "Anna", aneczka: "Anna",
  kasia: "Katarzyna", katarzyna: "Katarzyna",
  tomek: "Tomasz",
  piotrek: "Piotr", piotr: "Piotr",
  bartek: "Bartosz", bartosz: "Bartosz",
  wojtek: "Wojciech", wojciech: "Wojciech",
  lukasz: "Lukasz",
  agnieszka: "Agnieszka", aga: "Agnieszka",
  marek: "Marek",
  anna: "Anna",
};

const RECORD_TYPE_WORDS: Record<string, "conversation" | "report" | "vision"> = {
  rozmowa: "conversation", rozmowy: "conversation",
  raporty: "report", raportow: "report",
  zdjecie: "vision", zdjecia: "vision", foto: "vision", wizja: "vision"
};

const STOPWORDS = new Set([
  "z", "w", "u", "o", "i", "a", "z", "ze", "we", "na", "do", "od", "po",
  "za", "przez", "dla", "bez", "o", "po", "nad", "pod", "przed", "za",
  "znajdz", "znajdz", "pokaz", "pokaz", "szukaj", "wyszukaj", "lista",
  "wszystkie", "wszystkich", "jakie", "ktore", "ktora", "kto", "co", "gdzie",
  "kiedy", "ile"
]);

function norm(s: string): string {
  return toAscii(s.toLowerCase().replace(/[^a-ząćęłńóśźż\s0-9-]/gi, ""));
}

function dayStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function dayEnd(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

function iso(d: Date): string {
  return d.toISOString();
}

export function parseOfflineQuery(raw: string, now = new Date()): OfflineParsedQuery {
  const q = raw.trim();
  const qn = norm(q);

  let dateStart: string | undefined;
  let dateEnd: string | undefined;

  // --- dates ---
  if (qn.includes("dzisiaj") || qn.includes("dzis")) {
    dateStart = iso(dayStart(now));
    dateEnd = iso(dayEnd(now));
  } else if (qn.includes("wczoraj")) {
    const y = new Date(now);
    y.setDate(now.getDate() - 1);
    dateStart = iso(dayStart(y));
    dateEnd = iso(dayEnd(y));
  } else if (qn.includes("jutro")) {
    const t = new Date(now);
    t.setDate(now.getDate() + 1);
    dateStart = iso(dayStart(t));
    dateEnd = iso(dayEnd(t));
  } else if (/\bw\s+tym\s+tygodniu\b/.test(qn)) {
    const mon = new Date(now);
    mon.setDate(now.getDate() - now.getDay() + 1);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    dateStart = iso(dayStart(mon));
    dateEnd = iso(dayEnd(sun));
  } else if (/\bw\s+zeszlym\s+tygodniu\b/.test(qn)) {
    const mon = new Date(now);
    mon.setDate(now.getDate() - now.getDay() - 6);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    dateStart = iso(dayStart(mon));
    dateEnd = iso(dayEnd(sun));
  } else if (/\bw\s+tym\s+miesiacu\b/.test(qn)) {
    dateStart = iso(dayStart(new Date(now.getFullYear(), now.getMonth(), 1)));
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    dateEnd = iso(new Date(next.getTime() - 1));
  } else if (qn.includes("tydzien temu")) {
    const d = new Date(now);
    d.setDate(now.getDate() - 7);
    dateStart = iso(dayStart(d));
    dateEnd = iso(dayEnd(d));
  } else {
    for (const [dayName, dayNum] of Object.entries(WEEKDAYS)) {
      if (new RegExp(`\\bw\\s+${dayName}\\b`).test(qn)) {
        const diff = (now.getDay() - dayNum + 7) % 7;
        const d = new Date(now);
        d.setDate(now.getDate() - diff);
        dateStart = iso(dayStart(d));
        dateEnd = iso(dayEnd(d));
        break;
      }
    }
  }

  // absolute date: "10 czerwca 2026" or "10 czerwca"
  if (!dateStart) {
    for (const [monthName, monthNum] of Object.entries(MONTHS)) {
      const m = new RegExp(`(\\d{1,2})\\s+${monthName}(?:\\s+(\\d{4}))?`).exec(qn);
      if (m) {
        const day = parseInt(m[1], 10);
        const year = m[2] ? parseInt(m[2], 10) : now.getFullYear();
        const d = new Date(year, monthNum - 1, day);
        if (!isNaN(d.getTime())) {
          dateStart = iso(dayStart(d));
          dateEnd = iso(dayEnd(d));
        }
        break;
      }
    }
  }

  // iso "2026-06-10"
  if (!dateStart) {
    const isoMatch = /(\d{4}-\d{2}-\d{2})/.exec(q);
    if (isoMatch) {
      const d = new Date(isoMatch[1]);
      if (!isNaN(d.getTime())) {
        dateStart = iso(dayStart(d));
        dateEnd = iso(dayEnd(d));
      }
    }
  }

  // --- record type ---
  let recordType: "conversation" | "report" | "vision" | undefined;
  for (const [word, type] of Object.entries(RECORD_TYPE_WORDS)) {
    if (qn.includes(word)) {
      recordType = type;
      break;
    }
  }

  // --- person ---
  let person: string | undefined;
  const words = q.split(/\s+/);
  for (const w of words) {
    const clean = w.replace(/[^\w]/g, "");
    const n = norm(clean);
    if (clean.length >= 3 && clean[0].toUpperCase() === clean[0] && clean === clean.replace(/[^a-zA-Z]/g, '') && clean !== clean.toUpperCase()) {
      person = NAME_ALIASES[n] ?? clean;
      break;
    }
  }

  // --- arrangement detection ---
  const isArrangement = ARRANGEMENT_MARKERS.some(m => qn.includes(m));

  // --- topic filter from arrangement ---
  let topicFilter: string | undefined;
  if (isArrangement) {
    let lastVerbIdx = -1;
    for (const verb of ARRANGEMENT_VERBS) {
      const re = new RegExp(`\\b${verb}`);
      const m = re.exec(qn);
      if (m && m.index > lastVerbIdx) {
        lastVerbIdx = m.index + m[0].length;
      }
    }
    if (lastVerbIdx !== -1) {
      const tail = qn.slice(lastVerbIdx).trim();
      const tailWords = tail.split(/\s+/).filter(w => w.length > 1 && !ARRANGEMENT_STOPWORDS.has(w)).slice(0, 4);
      if (tailWords.length) topicFilter = tailWords.join(" ");
    }
  }

  // --- location/topic from query ---
  let locationFilter: string | undefined;
  const locationMarkers = ["w hali", "w magazynie", "w laboratorium", "na rampie", "na hali", "w biurze", "hala", "magazyn"];
  for (const m of locationMarkers) {
    if (qn.includes(m)) {
      locationFilter = m.replace(/^w\s+|^na\s+/, "").trim();
      break;
    }
  }

  // --- limit ---
  let limit = 10;
  const m1 = /\b(\d+)\s*(ostatni|ostatnia|ostatnie|najnowsze|najnowsza)\b/i.exec(q);
  if (m1) limit = parseInt(m1[1], 10);
  else {
    const m2 = /\b(ostatni|ostatnia|ostatnie)\s+(\d+)\b/i.exec(q);
    if (m2) limit = parseInt(m2[2], 10);
    else {
      const m3 = /\bpokaz\s+(\d+)\b/i.exec(qn);
      if (m3) limit = parseInt(m3[1], 10);
    }
  }

  // --- tokens for keyword search ---
  const rawTokens = tokenize(q);
  const tokens = rawTokens.filter(t => t.length > 1 && !STOPWORDS.has(t));

  return {
    text: q,
    recordType,
    person,
    dateStart,
    dateEnd,
    isArrangementQuery: isArrangement,
    topicFilter,
    locationFilter,
    limit,
    tokens
  };
}
